import React from 'react';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset, sendPasswordResetEmail, applyActionCode, checkActionCode } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';

export default function ActionCodeRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode') || '';
  const continueUrl = params.get('continueUrl') || '/login?reset=success';

  const [email, setEmail] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [newPw, setNewPw] = React.useState('');
  const auth = getAuth();

  // Handle email verification - verify the code and redirect appropriately
  React.useEffect(() => {
    if (mode === 'verifyEmail' && oobCode) {
      setLoading(false); // Don't show loading UI for email verification (redirects immediately)
      
      (async () => {
        try {
          console.info('[ActionCodeRoute] Verifying email with code:', { mode, oobCode });
          
          // Check and apply the verification code
          await checkActionCode(auth, oobCode);
          await applyActionCode(auth, oobCode);
          
          console.info('[ActionCodeRoute] ✅ Email verified successfully');
          
          // If user is signed in, reload their state to get emailVerified=true
          if (user) {
            console.info('[ActionCodeRoute] User is signed in, reloading...');
            await user.reload();
            
            // Redirect to the continueUrl if present, or to login
            const redirectUrl = continueUrl && continueUrl !== '/login?reset=success' 
              ? continueUrl 
              : '/login?verified=true';
            
            console.info('[ActionCodeRoute] Redirecting to:', redirectUrl);
            navigate(redirectUrl, { replace: true });
          } else {
            // User is not signed in - redirect to login
            console.info('[ActionCodeRoute] User not signed in, redirecting to login');
            navigate('/login?verified=true', { replace: true });
          }
        } catch (err: unknown) {
          const codeStr = typeof err === 'object' && err && 'code' in err ? String((err as { code?: string }).code || '') : '';
          console.error('[ActionCodeRoute] ❌ Email verification failed:', codeStr || err);
          
          // Provide specific error messages
          const errorParam = codeStr.includes('expired') ? 'expired' 
            : codeStr.includes('invalid') ? 'invalid'
            : 'error';
          
          navigate(`/login?verified=${errorParam}`, { replace: true });
        }
      })();
    }
  }, [mode, oobCode, continueUrl, navigate, auth]);

  // Debug guard: catch project/API key mismatch quickly
  React.useEffect(() => {
    try {
      const linkApiKey = new URLSearchParams(window.location.search).get('apiKey');
      const appApiKey = (auth.app && (auth.app as unknown as { options?: { apiKey?: string } }).options?.apiKey) as string | undefined;
      if (linkApiKey && appApiKey && linkApiKey !== appApiKey) {
        console.error('[RESET] API key mismatch between link and initialized app', { linkApiKey, appApiKey });
      }
    } catch {
      // ignore
    }
  }, [auth]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      // Skip if this is an email verification request (handled by separate useEffect above)
      if (mode === 'verifyEmail') {
        return;
      }
      
      if (mode !== 'resetPassword') {
        console.warn('[ActionCodeRoute] Unsupported mode:', mode);
        setError('Unsupported action.');
        setLoading(false);
        return;
      }
      try {
        const em = await verifyPasswordResetCode(auth, oobCode);
        if (!alive) return;
        setEmail(em);
        setLoading(false);
      } catch (e: unknown) {
        if (!alive) return;
        const code: string | undefined = typeof e === 'object' && e && 'code' in e ? (e as { code?: string }).code : undefined;
        const msg = code === 'auth/expired-action-code'
          ? 'This reset link has expired. You can request a new one below.'
          : code === 'auth/invalid-action-code'
          ? 'This reset link is invalid or already used. Request a new one below.'
          : code === 'auth/unauthorized-continue-uri'
          ? 'The return URL isn’t authorized. Ask support or request a new link.'
          : 'We couldn’t validate this link.';
        setError(msg);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [auth, mode, oobCode]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await confirmPasswordReset(auth, oobCode, newPw);
      navigate(continueUrl, { replace: true });
    } catch (e: unknown) {
      const code: string | undefined = typeof e === 'object' && e && 'code' in e ? (e as { code?: string }).code : undefined;
      const msg = code === 'auth/weak-password' ? 'Password is too weak.' : 'We couldn’t reset the password.';
      setError(msg);
    }
  }

  async function onResend() {
    try {
      const target = email ?? params.get('email');
      if (!target) {
        setError('Email unknown for resend. Return to the login screen to request a reset.');
        return;
      }
      await sendPasswordResetEmail(auth, target, {
        url: `${window.location.origin}/auth/action`,
        handleCodeInApp: true,
      });
      setError('A new reset email has been sent.');
    } catch {
      setError('Could not send a new reset email. Try again in a moment.');
    }
  }

  // Don't show UI while redirecting for email verification
  if (loading && mode !== 'verifyEmail') {
    return <div className="p-6 text-base">Validating your reset link…</div>;
  }
  
  // For verifyEmail mode, return null (redirecting in useEffect)
  if (mode === 'verifyEmail') {
    return null;
  }

  if (error && !email) {
    return (
      <div className="max-w-md mx-auto p-6 space-y-4">
        <h1 className="text-xl font-semibold">Password reset link problem</h1>
        <p className="text-sm opacity-80">{error}</p>
        <button onClick={onResend} className="px-3 py-2 rounded-2xl border border-transparent bg-accent text-primaryForeground hover:opacity-90">
          Resend reset link
        </button>
      </div>
    );
  }

  // Show reset form
  return (
    <div className="max-w-md mx-auto p-6 space-y-4">
      <h1 className="text-xl font-semibold">Set a new password</h1>
      <p className="text-sm opacity-80">for <span className="font-mono">{email}</span></p>
      {error && (
        <div className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-md px-3 py-2" role="alert">{error}</div>
      )}
      <form onSubmit={onSubmit} className="space-y-3">
        <input
          id="pw"
          type="password"
          required
          minLength={8}
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          placeholder="New password"
          className="w-full rounded-xl p-3 sm:text-sm border border-brand-orange bg-slate-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
        />
        <button type="submit" className="w-full px-3 py-2 rounded-2xl border border-transparent bg-accent text-primaryForeground hover:opacity-90">
          Update password
        </button>
      </form>

      <div className="text-xs opacity-70">
        If this page was opened automatically (email/security scanners), you may need to request a fresh link.
      </div>
    </div>
  );
}
